import { DeleteOutlined, EditOutlined, KeyOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Flex, message, Modal, Spin, Tooltip, Typography } from "antd";
import { useForm } from "antd/es/form/Form";
import _ from "lodash";
import { useState } from "react";
import styled from "styled-components";
import { ContentPartFilter } from "../../../components/content-part/ContentPartFilter";
import { PageHeader } from "../../../components/page-header";
import { UsersFilter } from "../../../models/filters/usersFilter";
import { EntUser } from "../../../models/user";
import { AddUserModal } from "./AddUserModal";
import { ChangePassUserModal } from "./ChangePassUserModal";
import { FilterUsersFarm } from "./FilterUsersFarm";
import { useUsers } from "./useUsers";
import { useTranslation } from "react-i18next";
import { useGetDataOnScroll } from "../../../components/getDataOnScrollProps/useGetDataOnScrollProps";
import { useLazyGetUserListQuery } from "../../../redux/api/userApis/usersApi";
import { AuthSliceMethods } from "../../../redux/slices/authSlice";
import { AppDispatch } from "../../../redux/root/root.store";
import { useDispatch } from "react-redux";
import { UnblockUserModal } from "./UnblockUserModal";

enum FormField {
  fullName,
  pin,
  phoneNumber,
  rolesIds,
  positionIds,
  departmentIds,
  governmentIds
}

export const Users = () => {
  const [modal, contextHolder] = Modal.useModal();
  const dispatch: AppDispatch = useDispatch();
  const [form] = useForm();
  const { t } = useTranslation()
  const [selectedUser, setSelectedUser] = useState<EntUser>(new EntUser());
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showModalPas, setShowModalPas] = useState<boolean>(false);
  const [showBlockedUsersModal, setShowBlockedUsersModal] = useState<boolean>(false);
  const { onAddUser, onEditUser, onDeleteUser, onChangePass } = useUsers(modal, () => {
	searchHandleClick();
  });
  const [height, setHeight] = useState<number>(0);

  const storedData = JSON.parse(localStorage.getItem("availableRoutes") || "[]");

  const isChangePasswordAvailable = storedData.some((route: any) => (route.alias === "/user/admin/change-password" || route.alias === "/interactive-map-ws") && route.methodPost);
  const isBanAvailable = storedData.some((route: any) => (route.alias === "/admin-panel/ban" || route.alias === "/interactive-map-ws") && route.methodPost);
  const isUnbanAvailable = storedData.some((route: any) => (route.alias === "/admin-panel/unban" || route.alias === "/interactive-map-ws") && route.methodPost);

  const openAddModal = (user: EntUser) => {
	setSelectedUser(user);
	setShowModal(true);
  }
  const AlertButton = styled(Button)`
      background: #ffc107 !important;
      border-color: #ffc107;
  `;

  const [getArchiveFolders] = useLazyGetUserListQuery();
  const {
	filter,
	fetching,
	access,
	data,
	totalItems,
	setData,
	setFetching,
	setFilter,
	scrollHandler,
	setCurrentPage
  } = useGetDataOnScroll<EntUser, UsersFilter>({
	initialFilter: new UsersFilter(),
	query: getArchiveFolders
  });

  const openChangeModal = (user: EntUser) => {
	setSelectedUser(user);
	setShowModalPas(true);
  };

  const setEntityFromFields = (): UsersFilter => {
	let user: UsersFilter = _.clone(filter);
	user.fullName = form.getFieldValue(FormField.fullName) ?? null;
	user.pin = form.getFieldValue(FormField.pin) ?? null;
	user.rolesIds = form.getFieldValue(FormField.rolesIds) ?? null;
	user.positionIds = form.getFieldValue(FormField.positionIds) ?? null;
	user.departmentIds = form.getFieldValue(FormField.departmentIds) ?? null;
	user.governmentIds = form.getFieldValue(FormField.governmentIds) ?? null;
	user.phoneNumber = form.getFieldValue(FormField.phoneNumber) ?? null;
	user.page = 1;
	return user;
  }
  const clearFields = () => {
	form.setFields([
	  { name: FormField.pin, value: null },
	  { name: FormField.fullName, value: null },
	  { name: FormField.phoneNumber, value: null },
	  { name: FormField.positionIds, value: null },
	  { name: FormField.rolesIds, value: null },
	  { name: FormField.departmentIds, value: null },
	  { name: FormField.governmentIds, value: null },
	  { name: FormField.phoneNumber, value: null },
	]);
  }
  const searchHandleClick = () => {
	setFilter(setEntityFromFields());
	setData([]);
	setCurrentPage(0);
	setFetching(true);
  }

  const toggleBlockUser = async (user: EntUser) => {
	if (!user) return;

	const updatedUser = { ...user, isBanned: true };
	setData((prevData) =>
	  prevData.map((u) => (u.id === user.id ? updatedUser : u))
	);

	try {
	  await dispatch(AuthSliceMethods.block(user));
	  message.success(t('user.blockSuccess'));
	  searchHandleClick();
	} catch (error) {
	  setData((prevData) =>
		prevData.map((u) => (u.id === user.id ? user : u))
	  );
	  message.error(t('user.actionFailed'));
	}
  };

  const confirmBlock = (user: EntUser) => {
	Modal.confirm({
	  title: t('user.blockConfirmation'),
	  content: t('user.blockConfirmationMessage'),
	  okText: t('general.confirm'),
	  cancelText: t('general.cancel'),
	  onOk: () => toggleBlockUser(user),
	});
  };

  const table = (
	<div style={ { maxHeight: `${ height }px`, overflow: 'auto' } } onScroll={ scrollHandler }>
	  <table>
		<thead>
		<tr>
		  <th style={ { width: "40px" } }>#</th>
		  <th style={ { width: "100px" } }>{ t('user.surname') }</th>
		  <th style={ { width: "100px" } }>{ t('user.name_employee') }</th>
		  <th style={ { width: "100px" } }>{ t('user.access_time') }</th>
		  <th style={ { width: "180px" } }></th>
		</tr>
		</thead>
		<tbody>
		{ fetching ?
		  <tr>
			<th colSpan={ 5 }>
			  <Spin spinning={ fetching } style={ {
				width: '100%',
				height: '180px',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center'
			  } }/>
			</th>
		  </tr>
		  : <></> }
		{ data.map((item, index) => (
		  <tr key={ item.id } onDoubleClick={ () => {
			openAddModal(item)
		  } }>
			<td>{ ++index }</td>
			<td>{ item?.secondName }</td>
			<td>{ item?.name }</td>
			<td>{ item?.temporaryAccessUntilTime }</td>
			<td>
			  <Flex gap="small">
				{ access && access.methodPut && (
				  <Tooltip title={ t('general.edit') }>
					<Button className='btn_edit' onClick={ () => {
					  openAddModal(item)
					} } type="primary" shape="circle" icon={ <EditOutlined/> }/>
				  </Tooltip>
				) }
				{ access && access.methodDelete && (
				  <Tooltip title={ t('general.delete') }>
					<Button className='btn_edit' danger onClick={ () => {
					  onDeleteUser(item)
					} } type="primary" shape="circle" icon={ <DeleteOutlined/> }/>
				  </Tooltip>
				) }
				{ isChangePasswordAvailable && (
				  <Tooltip title={ t('auth.change_password') }>
					<AlertButton className='btn_edit' danger onClick={ () => {
					  openChangeModal({ ...item, passwordLength: item.roles?.[0]?.passwordLength });
					} } type="primary" icon={ <KeyOutlined/> }>{ t('auth.change_password') }</AlertButton>
				  </Tooltip>
				) }
				{ isBanAvailable && (
				  <Tooltip title={ t('user.block') }>
					<Button
					  className="btn_edit"
					  danger={ true }
					  type="primary"
					  onClick={ () => confirmBlock(item) }
					>
					  { t('user.block') }
					</Button>
				  </Tooltip>
				) }
			  </Flex>
			</td>
		  </tr>
		)) }
		</tbody>
	  </table>
	</div>
  );
  return (
	<>
	  <PageHeader
		title={ t('titles.users') }
		additionalInfo={
		  <Typography.Text style={ { marginBottom: '0.5em', display: 'block' } } type="secondary">
			{ t('user.users_system') } { t('general.records_found') } { totalItems }
		  </Typography.Text>
		}/>
	  { access && access.methodPost && (
		<Button style={ { marginRight: 10 } } type="primary" icon={ <PlusOutlined/> } onClick={ () => {
		  openAddModal(new EntUser)
		} }>
		  { t('user.add_users') }
		</Button>
	  ) }
	  { isUnbanAvailable && (
		<Button
		  type="primary"
		  icon={ <KeyOutlined/> }
		  onClick={ () => {
			setShowBlockedUsersModal(true);
		  } }
		>
		  { t('user.view_blocked_users') }
		</Button>
	  ) }
	  <ContentPartFilter
		filter={ <FilterUsersFarm onSearch={ searchHandleClick }
								  onReset={ () => {
									clearFields();
									searchHandleClick()
								  } } formField={ FormField } form={ form }/> }
		setHeight={ setHeight }
		table={ table }
	  />
	  <AddUserModal
		user={ selectedUser }
		show={ showModal }
		onHide={ () => {
		  setShowModal(false)
		} }
		onSubmit={ selectedUser.id !== 0 ? onEditUser : onAddUser }
	  />
	  <ChangePassUserModal
		user={ selectedUser }
		passwordLength={ selectedUser.roles?.[0]?.passwordLength }
		show={ showModalPas }
		onHide={ () => {
		  setShowModalPas(false)
		} }
		onSubmit={ onChangePass }
	  />
	  <UnblockUserModal
		show={ showBlockedUsersModal }
		onHide={ () => setShowBlockedUsersModal(false) }
		onUnblockSuccess={ searchHandleClick }
	  />
	  { contextHolder }
	</>
  )
}
